/**
 * Pure classification of a plan tier + the CTA to show for it, so the routing
 * (checkout vs portal vs contact-sales vs free) is unit-testable without the
 * page's providers.
 */

export type PlanTier = 'free' | 'paid' | 'sales';

/**
 * free = no charge; paid = self-serve Stripe subscription; sales = custom /
 * sales-led (price not fixed, quoted per vendor).
 *
 * Prefers the explicit `pricingType` set on the plan; for plans saved before
 * it existed: has a Stripe price → paid, otherwise sales.
 *
 * Being the default plan no longer means free: the default is Enterprise (the
 * unlimited fallback for vendors without a plan), and the Free plan says
 * `pricingType: 'free'` itself. Reading the default as free priced Enterprise
 * at "Free" on the upgrade page.
 */
export const classifyPlan = (p: {
  isDefault?: boolean;
  slug?: string;
  stripePriceId?: string;
  pricingType?: 'free' | 'trial' | 'paid' | 'custom';
}): PlanTier => {
  // A trial costs nothing: it reads, and is offered, as free.
  if (p.pricingType === 'free' || p.pricingType === 'trial') return 'free';
  if (p.pricingType === 'paid') return 'paid';
  if (p.pricingType === 'custom') return 'sales';
  if (p.slug === 'free') return 'free';
  return p.stripePriceId ? 'paid' : 'sales';
};

export type CtaKind = 'current' | 'portal' | 'checkout' | 'sales' | 'free' | 'downgrade';

/**
 * Which CTA a plan card shows:
 * - current plan → 'current' (disabled)
 * - a lower tier than the current plan → 'downgrade' (disabled for now)
 * - vendor already subscribed → 'portal' (manage in Stripe Billing Portal)
 * - otherwise by tier → paid: 'checkout', sales: 'sales', free: 'free'
 */
export const resolveCtaKind = (
  plan: { isDefault?: boolean; slug?: string; stripePriceId?: string; pricingType?: 'free' | 'trial' | 'paid' | 'custom' },
  opts: { isCurrent: boolean; hasActiveSubscription: boolean; isDowngrade?: boolean },
): CtaKind => {
  if (opts.isCurrent) return 'current';
  // Downgrades are disabled for now — regardless of subscription state.
  if (opts.isDowngrade) return 'downgrade';
  if (opts.hasActiveSubscription) return 'portal';
  const tier = classifyPlan(plan);
  if (tier === 'paid') return 'checkout';
  if (tier === 'sales') return 'sales';
  return 'free';
};
